import { convertUtcToLocalTimezone } from "../../../../utils/time/time.js";
import { isBefore, differenceInMinutes, isAfter, format, setHours, parseISO, add } from "date-fns";
import { isEqual, merge } from "lodash";
import { DATE_FORMAT, CLINIC_STARTING_HOUR, CLINIC_ENDING_HOUR } from "../appointment-schedule.constants.js";
import { GenericEventType } from "../appointment-schedule.types.js";
import { toEntities } from "./common.js";
import "react";
import "date-fns/locale";
import "fast-memoize";
const parseAppointemnts = (appointments) => appointments.map(
  ({
    startTime,
    endTime,
    employeeReferenceId,
    patient,
    title,
    cancellationType,
    referenceId,
    state,
    colorCode,
    blocking,
    treatmentReferenceId,
    treatmentName,
    comment,
    risky,
    additionalAppointmentHints
  }) => ({
    referenceId,
    employeeReferenceId,
    patient,
    start: parseISO(startTime),
    end: parseISO(endTime),
    name: title,
    type: GenericEventType.APPOINTMENT,
    cancellationType,
    state,
    colorCode,
    blocking,
    treatmentReferenceId,
    treatmentName,
    comment,
    risky,
    additionalAppointmentHints
  })
);
const sortByStartTime = (a, b) => {
  const firstElementTime = a.start;
  const secondElementTime = b.start;
  if (isBefore(firstElementTime, secondElementTime))
    return -1;
  if (isBefore(secondElementTime, firstElementTime))
    return 1;
  return 0;
};
const sortByDuration = (a, b) => {
  const firstElementDuration = differenceInMinutes(a.end, a.start);
  const secondElementDuration = differenceInMinutes(b.end, b.start);
  if (firstElementDuration > secondElementDuration)
    return 1;
  if (firstElementDuration < secondElementDuration)
    return -1;
  return 0;
};
const sortByCancellationState = (accumulator, group) => {
  return [
    ...accumulator,
    group.sort((a, b) => {
      if (!a.cancellationType && !b.cancellationType)
        return 0;
      return a.cancellationType ? 1 : -1;
    })
  ];
};
const groupByTime = (previousAppointments, appointment) => {
  const previousGroup = previousAppointments[previousAppointments.length - 1];
  if (!previousGroup)
    return [[appointment]];
  const isOverlapping = previousAppointments.findIndex(
    (group) => group.some((previousAppointment) => isBefore(appointment.start, previousAppointment.end))
  );
  if (isOverlapping >= 0) {
    const groupsBefore = previousAppointments.slice(0, isOverlapping);
    const groupsAfter = previousAppointments.slice(isOverlapping + 1);
    const firstOverlappingGroup = previousAppointments[isOverlapping];
    return [...groupsBefore, [...firstOverlappingGroup, appointment], ...groupsAfter];
  }
  return [...previousAppointments, [appointment]];
};
const groupBy = (getKey) => (items) => {
  return items.reduce((grouped, item) => {
    const key = getKey(item);
    const currentGroup = grouped[key] || [];
    return {
      ...grouped,
      [key]: [...currentGroup, item]
    };
  }, {});
};
const groupOverlappingAppointments = (nonWorkingTimes) => (appointments) => {
  return Object.entries(nonWorkingTimes).reduce(
    (acc, [date, value]) => ({
      ...acc,
      [date]: Object.entries(value).reduce((currentAcc, [employeeId, currentValue]) => {
        var _a;
        const blockers = (currentValue == null ? void 0 : currentValue.filter(({ type }) => type === GenericEventType.BLOCKER)) ?? [];
        const parsedAppointments = parseAppointemnts(((_a = appointments == null ? void 0 : appointments[date]) == null ? void 0 : _a[employeeId]) ?? []);
        const data = [...parsedAppointments, ...blockers];
        return {
          ...currentAcc,
          [employeeId]: data.sort(sortByStartTime).reduce(groupByTime, []).reduce(sortByCancellationState, [])
        };
      }, {})
    }),
    {}
  );
};
const groupNonOverlappingAppointments = (appointmentsGroup) => appointmentsGroup.reduce(
  (previousGroups, appointment, index) => {
    if (!index)
      return [[appointment]];
    const isNotOverlapping = previousGroups.findIndex(
      (previousGroup) => previousGroup.every(({ end }) => isAfter(appointment.start, end) || isEqual(appointment.start, end))
    );
    if (isNotOverlapping >= 0) {
      const groupsBefore = previousGroups.slice(0, isNotOverlapping);
      const groupsAfter = previousGroups.slice(isNotOverlapping + 1);
      const firstOverlappingGroup = previousGroups[isNotOverlapping];
      return [...groupsBefore, [...firstOverlappingGroup, appointment], ...groupsAfter];
    }
    return [...previousGroups, [appointment]];
  },
  [[]]
);
const groupByEmployee = (data) => {
  return Object.entries(data).reduce(
    (accumulator, [key, value]) => ({
      ...accumulator,
      [key]: toEntities(value, "employeeReferenceId", true)
    }),
    {}
  );
};
const groupWorkingTimesByDate = (workingTimes = []) => {
  const flatentWorkingTimes = workingTimes == null ? void 0 : workingTimes.flatMap((employee) => {
    var _a;
    return (_a = employee == null ? void 0 : employee.workingTimes) == null ? void 0 : _a.flatMap((workingTime) => {
      const { date } = workingTime;
      return workingTime.periods.map(({ start, end }) => {
        return {
          start: /* @__PURE__ */ new Date(`${date}T${start}`),
          end: /* @__PURE__ */ new Date(`${date}T${end}`),
          employeeReferenceId: employee.employeeReferenceId ?? ""
        };
      });
    });
  });
  return groupBy((workingTime) => format(workingTime.start, DATE_FORMAT))(flatentWorkingTimes);
};
const groupNonWorkingTimesByDate = (data = []) => {
  const startHourPadded = CLINIC_STARTING_HOUR.toString().padStart(2, "0");
  const endHourPadded = CLINIC_ENDING_HOUR.toString().padStart(2, "0");
  const filledEmptyDays = data == null ? void 0 : data.flatMap(
    (employee) => {
      var _a;
      return (_a = employee == null ? void 0 : employee.workingTimes) == null ? void 0 : _a.flatMap((workingTime) => {
        if (workingTime.periods.length)
          return [];
        return {
          start: setHours(parseISO(workingTime.date), CLINIC_STARTING_HOUR),
          end: setHours(parseISO(workingTime.date), CLINIC_ENDING_HOUR),
          employeeReferenceId: employee.employeeReferenceId
        };
      });
    }
  );
  const flattenNonWorkingTimes = data == null ? void 0 : data.flatMap(
    ({ nonWorkingTimes, employeeReferenceId }) => {
      return nonWorkingTimes.flatMap(
        ({ date, periods }) => periods.reduce(
          (accumulator, { start, end, type, title, workRelated, referenceId }) => {
            const parsedStartTime = convertUtcToLocalTimezone(start ?? "");
            const parsedEndTime = convertUtcToLocalTimezone(end ?? "");
            const [startHour, startMinutes, startSeconds] = parsedStartTime.split(":");
            const [endHour, endMinutes, endSeconds] = parsedEndTime.split(":");
            try {
              const shouldFilterPeriod = Number(startHour) < Number(startHourPadded) || Number(startHour) >= Number(endHourPadded) || Number(endHour) >= Number(endHourPadded);
              if (shouldFilterPeriod)
                return accumulator;
              return [
                ...accumulator,
                {
                  start: add(parseISO(date), {
                    hours: Number(startHour),
                    minutes: Number(startMinutes.slice(0, 2)),
                    seconds: Number(startSeconds)
                  }),
                  end: add(parseISO(date), {
                    hours: Number(endHour),
                    minutes: Number(endMinutes.slice(0, 2)),
                    seconds: Number(endSeconds)
                  }),
                  employeeReferenceId,
                  type,
                  title,
                  workRelated,
                  referenceId
                }
              ];
            } catch (error) {
              console.error("Error parsing date/time:", error);
              return accumulator;
            }
          },
          []
        )
      );
    }
  );
  const elements = [...flattenNonWorkingTimes ?? [], ...filledEmptyDays ?? []];
  return groupBy((nonWorkingTime) => format(nonWorkingTime.start, DATE_FORMAT))(elements);
};
const groupNonWorkingTimes = (...groups) => {
  const mergedData = merge({}, ...groups);
  return Object.entries(mergedData).reduce(
    (acc, [day, employeeIds]) => ({
      ...acc,
      [day]: Object.entries(employeeIds).reduce((data, [employeeId]) => {
        const nonWorkingTimes = (groups == null ? void 0 : groups.map((group) => {
          var _a;
          return ((_a = group == null ? void 0 : group[day]) == null ? void 0 : _a[employeeId]) ?? [];
        }).flat()) ?? [];
        return {
          ...data,
          [employeeId]: nonWorkingTimes
        };
      }, {})
    }),
    {}
  );
};
export {
  groupBy,
  groupByEmployee,
  groupNonOverlappingAppointments,
  groupNonWorkingTimes,
  groupNonWorkingTimesByDate,
  groupOverlappingAppointments,
  groupWorkingTimesByDate,
  sortByDuration,
  sortByStartTime
};
